import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  updateObjectDesign,
  updateObjectInfo,
  subscribeToDDLCollections,
  subscribeToInspectionsByObject,
  subscribeToDropdownRegistrations,
  deleteObject,
} from '../utils/storage';
import { validateObjectUrl, validateDrivePath } from '../utils/urlValidation';
import { getPermissions } from '../utils/permissions';
import AccessDeniedModal from './AccessDeniedModal';
import DiscardConfirmModal from './DiscardConfirmModal';
import { useReadOnly } from '../contexts/ReadOnlyContext';
import './EditObjectModal.css';
import './Modal.css';
import shapeImgCube     from '../assets/shapes/cube.png';
import shapeImgCylinder from '../assets/shapes/cylinder.png';
import shapeImgNoImage  from '../assets/shapes/no-image.png';
import shapeImgPill     from '../assets/shapes/pill.png';
import shapeImgSphere   from '../assets/shapes/sphere.png';

const SHAPE_IMAGE_MAP = {
  cube:     shapeImgCube,
  cylinder: shapeImgCylinder,
  pill:     shapeImgPill,
  sphere:   shapeImgSphere,
};

const PAGES = ['INFO', 'Design', 'Sizing', 'Construction', 'Miscellaneous'];

// Maps DDL registration IDs → local state keys (all pages)
const DDL_STATE_MAP = {
  // INFO
  'object_type':                   'object_type',
  // Design
  'design_objectSpec':             'objectSpec',
  'design_attributeA':             'attributeA',
  'design_attributeAType':         'attributeAType',
  'design_attributeB':             'attributeB',
  'design_attributeBType':         'attributeBType',
  // Sizing
  'sizing_dimensionA':             'dimensionA',
  'sizing_dimensionB':             'dimensionB',
  'sizing_objectCloselyResembles': 'objectCloselyResembles',
  // Construction
  'construction_buildingStandard': 'buildingStandard',
  'construction_material1':        'material1',
  'construction_material1Type':    'material1Type',
  'construction_material2':        'material2',
  'construction_material2Type':    'material2Type',
  'construction_insulation':       'insulation',
  // Miscellaneous
  'misc_heatingElement':           'heatingElement',
  'misc_structuralSupport':        'structuralSupport',
  'misc_yearOfConstruction':       'yearOfConstruction',
  'misc_previousTIYear':           'previousTIYear',
};

// Design pressure / temperature accept only 0-9 . + - /  — the key filter
// blocks other keys; this also strips them from pasted text.
const DESIGN_VALUE_DISALLOWED = /[^0-9.+\-/]/g;

export default function EditObjectModal({ object, onClose, onDeleted }) {
  const { userProfile } = useAuth();
  const perms = getPermissions(userProfile?.roles || []);
  const isAdmin = !!userProfile?.roles?.includes('admin');
  const readOnly = useReadOnly();   // archived → "Show more": inputs disabled, no delete/save
  const [showDeleteDenied, setShowDeleteDenied] = useState(false);
  // Admin-only: confirm deleting an object that still has inspections.
  const [showDeleteWithInspections, setShowDeleteWithInspections] = useState(false);
  const [page, setPage]         = useState(0);
  const [saving, setSaving]     = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isDirty, setIsDirty]   = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [showDiscard, setShowDiscard] = useState(false);
  const [inspectionCount, setInspectionCount] = useState(null);

  // ── Page 0 (INFO) state ────────────────────────────────────────
  const [infoType,         setInfoType]         = useState(object.type            || '');
  const [infoObjectTag,    setInfoObjectTag]    = useState(object.objectTag       || '');
  // Google Drive path: free-text. The old prefix-lock UX (forcing the
  // textbox to always start with "Shared drives\") was removed in
  // favour of paste-friendly validation on blur. See validateDrivePath
  // in utils/urlValidation.js.
  const [googleDrivePath,  setGoogleDrivePath]  = useState(object.googleDrivePath || '');
  const [drivePathHint,    setDrivePathHint]    = useState('');
  const drivePathInputRef = useRef(null);

  const [threeDInspectUrl, setThreeDInspectUrl] = useState(object.threeDInspectUrl || '');
  const [objectUrlHint,    setObjectUrlHint]    = useState('');
  const objectUrlInputRef = useRef(null);

  // ── Page 1 (Design) state ──────────────────────────────────────
  const existing = object.designSpec || {};

  const [objectSpec,     setObjectSpec]     = useState(existing.objectSpec     || '');
  const [attributeA,     setAttributeA]     = useState(existing.attributeA     || '');
  const [attributeAType, setAttributeAType] = useState(existing.attributeAType || '');
  const [attributeB,     setAttributeB]     = useState(existing.attributeB     || '');
  const [attributeBType, setAttributeBType] = useState(existing.attributeBType || '');

  const [radioVal,     setRadioVal]     = useState(existing.productStoredRadio || '');
  const [productText,  setProductText]  = useState(
    existing.productStoredRadio
      ? existing.productStoredRadio
      : (existing.productStored || '')
  );
  const [textEditable, setTextEditable] = useState(!existing.productStoredRadio);
  const productInputRef = useRef(null);

  // ── Page 2 (Sizing) state ──────────────────────────────────────
  const [dimensionA, setDimensionA] = useState(existing.dimensionA || '');

  const _dimASizeExisting = existing.dimASize || '';
  const [dimASizeNA, setDimASizeNA] = useState(_dimASizeExisting === 'Not available');
  const [dimASize,   setDimASize]   = useState(_dimASizeExisting === 'Not available' ? '' : _dimASizeExisting);

  const [dimensionB, setDimensionB] = useState(existing.dimensionB || '');

  const _dimBSizeExisting = existing.dimBSize || '';
  const [dimBSizeNA, setDimBSizeNA] = useState(_dimBSizeExisting === 'Not available');
  const [dimBSize,   setDimBSize]   = useState(_dimBSizeExisting === 'Not available' ? '' : _dimBSizeExisting);

  const [objectCloselyResembles, setObjectCloselyResembles] = useState(existing.objectCloselyResembles || '');

  // Sub-dimension inputs A / B / C (digits only)
  const [sizingA, setSizingA] = useState(existing.sizingA || '');
  const [sizingB, setSizingB] = useState(existing.sizingB || '');
  const [sizingC, setSizingC] = useState(existing.sizingC || '');

  // ── Page 3 (Construction) state ───────────────────────────────
  const [buildingStandard, setBuildingStandard] = useState(existing.buildingStandard || '');
  const [material1,        setMaterial1]        = useState(existing.material1        || '');
  const [material1Type,    setMaterial1Type]    = useState(existing.material1Type    || '');
  const [insulation,       setInsulation]       = useState(existing.insulation       || '');

  // "Second material not applicable" — checked by default (no second material)
  const [mat2NotApplicable, setMat2NotApplicable] = useState(
    !existing.material2 || existing.material2 === 'Not applicable'
  );
  const [material2,     setMaterial2]     = useState(existing.material2     || 'Not applicable');
  const [material2Type, setMaterial2Type] = useState(existing.material2Type || 'Not applicable');

  // Clockwise angle (degrees, 0–359) from True North to the manhole, viewed from top
  const [manholeNorthAngle, setManholeNorthAngle] = useState(
    existing.manholeNorthAngle === undefined || existing.manholeNorthAngle === null
      ? ''
      : String(existing.manholeNorthAngle)
  );

  // ── Page 4 (Miscellaneous) state ──────────────────────────────
  const [heatingElement,    setHeatingElement]    = useState(existing.heatingElement    || '');
  const [structuralSupport, setStructuralSupport] = useState(existing.structuralSupport || '');

  const [designPressureRadio,    setDesignPressureRadio]    = useState(existing.designPressureRadio || '');
  const [designPressureText,     setDesignPressureText]     = useState(
    existing.designPressureRadio ? existing.designPressureRadio : (existing.designPressure || '')
  );
  const [designPressureEditable, setDesignPressureEditable] = useState(!existing.designPressureRadio);
  const designPressureRef = useRef(null);

  const [designTempRadio,    setDesignTempRadio]    = useState(existing.designTempRadio || '');
  const [designTempText,     setDesignTempText]     = useState(
    existing.designTempRadio ? existing.designTempRadio : (existing.designTemp || '')
  );
  const [designTempEditable, setDesignTempEditable] = useState(!existing.designTempRadio);
  const designTempRef = useRef(null);

  const [yearOfConstruction, setYearOfConstruction] = useState(existing.yearOfConstruction || '');
  const [previousTIYear,     setPreviousTIYear]     = useState(existing.previousTIYear     || '');

  // ── DDL data ───────────────────────────────────────────────────
  // allCollections: full collection objects with nested items — single source for all dropdowns
  const [allCollections, setAllCollections] = useState([]);
  // ddlConfigs: relationship/pointTo/collectionId per dropdown ID
  const [ddlConfigs, setDdlConfigs] = useState({});

  // Snapshot of ALL dropdown values (all pages) for link/chain filtering
  const currentValues = {
    object_type:      infoType,
    // Design
    objectSpec, attributeA, attributeAType, attributeB, attributeBType,
    // Sizing
    dimensionA, dimensionB, objectCloselyResembles,
    // Construction
    buildingStandard, material1, material1Type, material2, material2Type, insulation,
    // Miscellaneous
    heatingElement, structuralSupport, yearOfConstruction, previousTIYear,
  };
  const setterMap = {
    object_type:      setInfoType,
    // Design
    objectSpec:       setObjectSpec,
    attributeA:       setAttributeA,
    attributeAType:   setAttributeAType,
    attributeB:       setAttributeB,
    attributeBType:   setAttributeBType,
    // Sizing
    dimensionA:               setDimensionA,
    dimensionB:               setDimensionB,
    objectCloselyResembles:   setObjectCloselyResembles,
    // Construction
    buildingStandard: setBuildingStandard,
    material1:        setMaterial1,
    material1Type:    setMaterial1Type,
    material2:        setMaterial2,
    material2Type:    setMaterial2Type,
    insulation:       setInsulation,
    // Miscellaneous
    heatingElement:      setHeatingElement,
    structuralSupport:   setStructuralSupport,
    yearOfConstruction:  setYearOfConstruction,
    previousTIYear:      setPreviousTIYear,
  };

  // ── Subscriptions ──────────────────────────────────────────────
  useEffect(() => {
    return subscribeToInspectionsByObject(object.id, (list) => {
      setInspectionCount(list.length);
    });
  }, [object.id]);

  useEffect(() => {
    return subscribeToDDLCollections(setAllCollections);
  }, []);

  useEffect(() => {
    return subscribeToDropdownRegistrations(setDdlConfigs);
  }, []);

  // ── Option computation ─────────────────────────────────────────

  /** Return sorted primary-level labels for a given dropdown ID, derived from allCollections. */
  function getDDLOptions(dropdownId) {
    const pinnedLower = ['not applicable', 'not available'];
    const isPinned = (it) => pinnedLower.includes(it.label.trim().toLowerCase());
    const matched = allCollections.find((c) => {
      if (!c.assignedTo) return false;
      if (Array.isArray(c.assignedTo)) return c.assignedTo.includes(dropdownId);
      return c.assignedTo === dropdownId;
    });
    if (!matched) return [];
    const items = matched.items || [];
    return [
      ...items.filter((it) => !isPinned(it)).sort((a, b) => a.label.localeCompare(b.label)),
      ...items.filter((it) => isPinned(it)),
    ].map((it) => it.label);
  }

  /**
   * Compute visible options for a design dropdown, respecting:
   *   none / link  → primary-level opts (with link exclusion)
   *   chain        → secondary or tertiary level from collection tree,
   *                  determined by whether the pointTo dropdown is itself chain
   */
  function getComputedOptions(ddlId) {
    const cfg = ddlConfigs[ddlId];

    // ── none or link: use primary-level opts derived from allCollections ──
    if (!cfg || cfg.relationship !== 'chain') {
      const allOpts = getDDLOptions(ddlId);
      // Apply link exclusion
      if (cfg?.relationship === 'link' && cfg.pointTo) {
        const linkedKey   = DDL_STATE_MAP[cfg.pointTo];
        const linkedValue = linkedKey ? (currentValues[linkedKey] || '') : '';
        return linkedValue ? allOpts.filter((o) => o !== linkedValue) : allOpts;
      }
      return allOpts;
    }

    // ── chain ─────────────────────────────────────────────────────
    if (!cfg.pointTo) return [];

    const parentDdlId   = cfg.pointTo;
    const parentCfg     = ddlConfigs[parentDdlId];
    const parentStateKey = DDL_STATE_MAP[parentDdlId];
    const parentValue   = (parentStateKey ? currentValues[parentStateKey] : '') || '';

    if (!parentValue) return []; // parent not yet selected

    // Find this dropdown's assigned collection
    const collId     = cfg.collectionId;
    const collection = collId ? allCollections.find((c) => c.id === collId) : null;
    if (!collection) return [];
    const items = collection.items || [];

    const isParentChain = parentCfg?.relationship === 'chain';

    if (!isParentChain) {
      // ── SECONDARY: parent is at primary level (none/link) ───────
      // Show children of parent's selected primary item
      const parentItem = items.find((it) => it.label === parentValue);
      return (parentItem?.children || []).map((it) => it.label);
    } else {
      // ── TERTIARY: parent is itself chain (secondary level) ───────
      // Need grandparent value to locate the correct branch
      const grandparentDdlId  = parentCfg.pointTo;
      const grandparentKey    = grandparentDdlId ? DDL_STATE_MAP[grandparentDdlId] : null;
      const grandparentValue  = (grandparentKey ? currentValues[grandparentKey] : '') || '';

      if (!grandparentValue) return [];

      // Walk down: primary item → secondary item → tertiary items
      const gpItem = items.find((it) => it.label === grandparentValue);
      if (!gpItem?.children) return [];

      const parentItemInChildren = gpItem.children.find((it) => it.label === parentValue);
      return (parentItemInChildren?.children || []).map((it) => it.label);
    }
  }

  // ── Dropdown change handler ────────────────────────────────────

  /**
   * Handle a design dropdown value change:
   *  • Link: if the new value matches the linked partner's selection, clear the partner
   *  • Chain: clear any downstream dropdowns chained to this one (and their dependents)
   */
  function handleDropdownChange(ddlId, newValue, setter) {
    setter(newValue);

    const cfg = ddlConfigs[ddlId];

    // Link clearing
    if (cfg?.relationship === 'link' && cfg.pointTo && newValue) {
      const linkedKey = DDL_STATE_MAP[cfg.pointTo];
      if (linkedKey && currentValues[linkedKey] === newValue) {
        setterMap[linkedKey]?.('');
      }
    }

    // Chain clearing — clear all dropdowns that pointTo this one, then their dependents
    for (const [otherDdlId, otherCfg] of Object.entries(ddlConfigs)) {
      if (otherCfg.relationship === 'chain' && otherCfg.pointTo === ddlId) {
        const otherKey = DDL_STATE_MAP[otherDdlId];
        if (otherKey) {
          setterMap[otherKey]?.('');
          // One more level (tertiary)
          for (const [grandDdlId, grandCfg] of Object.entries(ddlConfigs)) {
            if (grandCfg.relationship === 'chain' && grandCfg.pointTo === otherDdlId) {
              const grandKey = DDL_STATE_MAP[grandDdlId];
              if (grandKey) setterMap[grandKey]?.('');
            }
          }
        }
      }
    }
  }

  // ── Radio / product-stored logic ──────────────────────────────
  function handleRadioChange(val) {
    setRadioVal(val);
    setProductText(val);
    setTextEditable(false);
  }

  function handleProductDoubleClick() {
    setRadioVal('');
    setProductText('');
    setTextEditable(true);
    setTimeout(() => productInputRef.current?.focus(), 0);
  }

  function handleProductTextChange(e) {
    if (!textEditable) return;
    setProductText(e.target.value);
  }

  // ── Material 2 not-applicable checkbox ────────────────────────
  function handleMat2NotApplicable(checked) {
    setMat2NotApplicable(checked);
    if (checked) {
      setMaterial2('Not applicable');
      setMaterial2Type('Not applicable');
    } else {
      setMaterial2('');
      setMaterial2Type('');
    }
  }

  // ── Dimension size NA handlers ────────────────────────────────
  function handleDimASizeNA(checked) {
    setDimASizeNA(checked);
    if (!checked) setDimASize('');
  }
  function handleDimBSizeNA(checked) {
    setDimBSizeNA(checked);
    if (!checked) setDimBSize('');
  }

  // ── Design Pressure handlers ──────────────────────────────────
  function handleDesignPressureRadio(val) {
    setDesignPressureRadio(val);
    setDesignPressureText(val);
    setDesignPressureEditable(false);
  }
  function handleDesignPressureDoubleClick() {
    setDesignPressureRadio('');
    setDesignPressureText('');
    setDesignPressureEditable(true);
    setTimeout(() => designPressureRef.current?.focus(), 0);
  }
  function handleDesignPressureTextChange(e) {
    if (!designPressureEditable) return;
    setDesignPressureText(e.target.value.replace(DESIGN_VALUE_DISALLOWED, ''));
  }

  // ── Design Temperature handlers ───────────────────────────────
  function handleDesignTempRadio(val) {
    setDesignTempRadio(val);
    setDesignTempText(val);
    setDesignTempEditable(false);
  }
  function handleDesignTempDoubleClick() {
    setDesignTempRadio('');
    setDesignTempText('');
    setDesignTempEditable(true);
    setTimeout(() => designTempRef.current?.focus(), 0);
  }
  function handleDesignTempTextChange(e) {
    if (!designTempEditable) return;
    setDesignTempText(e.target.value.replace(DESIGN_VALUE_DISALLOWED, ''));
  }

  // ── Delete ────────────────────────────────────────────────────
  function handleDeleteObject() {
    if (!perms.canDeleteObject) {
      setShowDeleteDenied(true);
      return;
    }
    // Objects with inspections are normally undeletable; an admin can delete
    // them, but only after a dedicated confirmation (the delete cascades to
    // every inspection inside).
    if (inspectionCount > 0) {
      if (!isAdmin) return;   // button is disabled for non-admins anyway
      setShowDeleteWithInspections(true);
      return;
    }
    if (!window.confirm(`Delete "${infoObjectTag}"? This cannot be undone.`)) return;
    performDelete();
  }
  async function performDelete() {
    setShowDeleteWithInspections(false);
    setDeleting(true);
    try {
      await deleteObject(object.id);
      onDeleted?.();
      onClose();
    } catch (err) {
      console.error('Delete failed:', err);
      setDeleting(false);
    }
  }

  // ── Save ──────────────────────────────────────────────────────
  // Shared payload builders — used by both autoSave and handleSave
  function buildInfoPayload() {
    return {
      type: infoType,
      objectTag: infoObjectTag,
      googleDrivePath,
      threeDInspectUrl,
    };
  }

  function buildDesignPayload() {
    return {
      // Design
      objectSpec, attributeA, attributeAType, attributeB, attributeBType,
      productStoredRadio: radioVal || null,
      productStored: productText,
      // Sizing
      dimensionA,
      dimASize: dimASizeNA ? 'Not available' : dimASize,
      dimensionB,
      dimBSize: dimBSizeNA ? 'Not available' : dimBSize,
      objectCloselyResembles,
      sizingA: (() => { const s = objectCloselyResembles.toLowerCase().trim(); return ['cube','cylinder','pill','sphere'].includes(s) ? sizingA : ''; })(),
      sizingB: (() => { const s = objectCloselyResembles.toLowerCase().trim(); return ['cube','cylinder','pill'].includes(s) ? sizingB : ''; })(),
      sizingC: (() => { const s = objectCloselyResembles.toLowerCase().trim(); return ['cube','pill'].includes(s) ? sizingC : ''; })(),
      // Construction
      buildingStandard, material1, material1Type,
      material2, material2Type, insulation,
      // Empty input → 0 (rather than null) per user spec: every object
      // has SOME orientation reference, so we default to north-aligned
      // (0°) until the inspector overrides it.
      manholeNorthAngle: manholeNorthAngle === '' ? 0 : parseInt(manholeNorthAngle, 10),
      // Miscellaneous
      heatingElement, structuralSupport,
      designPressureRadio: designPressureRadio || null,
      designPressure: designPressureText,
      designTempRadio: designTempRadio || null,
      designTemp: designTempText,
      yearOfConstruction, previousTIYear,
    };
  }

  // Awaited save — called only on final "Save Changes" button
  // All data is kept in memory until the user explicitly saves.
  async function handleSave() {
    setSaving(true);
    try {
      await Promise.all([
        updateObjectInfo(object.id, buildInfoPayload()),
        updateObjectDesign(object.id, buildDesignPayload()),
      ]);
      onClose();
    } catch (err) {
      console.error('Failed to save:', err);
      setSaveError('Failed to save changes. Please check your connection and try again.');
      setSaving(false);
    }
  }

  // ── Shape / sizing derivations (for Sizing page render + computation) ─
  const shapeLower = objectCloselyResembles.toLowerCase().trim();
  const isCube     = shapeLower === 'cube';
  const isCylinder = shapeLower === 'cylinder';
  const isPill     = shapeLower === 'pill';
  const isSphere   = shapeLower === 'sphere';
  const shapeKnown = isCube || isCylinder || isPill || isSphere;
  const aEnabled   = shapeKnown;
  const bEnabled   = isCube || isCylinder || isPill;
  const cEnabled   = isCube || isPill;
  const shapeImageSrc = SHAPE_IMAGE_MAP[shapeLower] || shapeImgNoImage;

  const Amm = parseFloat(sizingA);
  const Bmm = parseFloat(sizingB);
  const Cmm = parseFloat(sizingC);
  const hasA = aEnabled && sizingA !== '' && !isNaN(Amm);
  const hasB = bEnabled && sizingB !== '' && !isNaN(Bmm);
  const hasC = cEnabled && sizingC !== '' && !isNaN(Cmm);

  let estSA = null, estVol = null;
  if (isCube && hasA && hasB && hasC) {
    const a = Amm / 1000, b = Bmm / 1000, c = Cmm / 1000;
    estVol = a * b * c;
    estSA  = 2 * (a * b + b * c + c * a);
  } else if (isCylinder && hasA && hasB) {
    const a = Amm / 1000, b = Bmm / 1000;
    estVol = Math.PI * (a * a / 4) * b;
    estSA  = (Math.PI * a * a) / 2 + Math.PI * a * b;
  } else if (isSphere && hasA) {
    const a = Amm / 1000;
    estVol = Math.PI * a * a * a / 6;
    estSA  = Math.PI * a * a;
  } else if (isPill && hasA && hasB && hasC) {
    const a = Amm / 1000, b = Bmm / 1000, c = Cmm / 1000;
    estVol = Math.PI * (a * a / 4) * b + (4 / 3) * Math.PI * c * c * c;
    estSA  = Math.PI * a * b + 4 * Math.PI * c * c;
  }

  // ── Render ────────────────────────────────────────────────────
  const handleClose = () => {
    if (isDirty) { setShowDiscard(true); return; }
    onClose();
  };

  return (
    <>
    <div className="modal-overlay">

      {/* Dirty tracking via bubbled onChange, not onInput: for a <select>,
          'input' fires before 'change', so the first setIsDirty re-render
          reset the select to its old value and the first pick was lost. */}
      <div className="modal modal-wide eom-modal" onClick={e => e.stopPropagation()} onChange={() => { setIsDirty(true); setSaveError(null); }}>

        {/* Header — shows live info values */}
        <div className="modal-header">
          <div className="eom-header-left">
            <span className="eom-object-id">{infoObjectTag || '—'}</span>
            <span className="eom-object-type">{infoType || '—'}</span>
          </div>
          <button className="modal-close" onClick={handleClose}>✕</button>
        </div>

        {/* Step bar */}
        <div className="eom-step-bar">
          {PAGES.map((label, i) => (
            <React.Fragment key={label}>
              <button
                className={`eom-step-btn${i === page ? ' active' : i < page ? ' done' : ''}`}
                onClick={() => setPage(i)}
                title={label}
              >
                <span className="eom-step-num">{i + 1}</span>
              </button>
              {i < PAGES.length - 1 && (
                <div className={`eom-step-connector${i < page ? ' done' : ''}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Read-only ("Show more") disables every field via the fieldset. */}
        <fieldset className="eom-fieldset" disabled={readOnly}>

        {/* ── Page 0: INFO ── */}
        {page === 0 && (
          <div className="eom-page">
            <h3 className="eom-page-title">Info</h3>
            <div className="form-group">
              <label>Object Type</label>
              <select
                className="assign-select-full"
                value={infoType}
                onChange={(e) => handleDropdownChange('object_type', e.target.value, setInfoType)}
              >
                <option value="">— Select —</option>
                {getDDLOptions('object_type').map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>

            <div className="form-group">
              <label>Object ID</label>
              <input
                type="text"
                className="assign-select-full"
                value={infoObjectTag}
                onChange={(e) => setInfoObjectTag(e.target.value)}
                placeholder="e.g. TK-101, V-203"
              />
            </div>

            <div className="form-group">
              <label>Google Drive Path</label>
              <input
                ref={drivePathInputRef}
                type="text"
                className="assign-select-full"
                value={googleDrivePath}
                onChange={(e) => {
                  // Clearing the hint on `change` (not `focus`) lets
                  // the hint survive the focus that comes from our
                  // own setTimeout refocus on a failed blur — without
                  // a typed character the user shouldn't get the
                  // "everything's fine" treatment.
                  if (drivePathHint) setDrivePathHint('');
                  setGoogleDrivePath(e.target.value);
                }}
                onBlur={() => {
                  // Empty → always OK. Otherwise: must contain
                  // "\Shared drives\". On success, strip the drive
                  // letter so the saved value is always anchored at
                  // "Shared drives\".
                  const r = validateDrivePath(googleDrivePath);
                  if (!r.ok) {
                    setDrivePathHint(r.hint);
                    // Refocus after the blur completes (zero-delay
                    // setTimeout avoids a focus/blur loop).
                    setTimeout(() => drivePathInputRef.current?.focus(), 0);
                    return;
                  }
                  setDrivePathHint('');
                  if (r.cleaned !== googleDrivePath) setGoogleDrivePath(r.cleaned);
                }}
                placeholder="G:\Shared drives\Projects A-N\..."
              />
              {drivePathHint && (
                <div className="form-field-hint-error">{drivePathHint}</div>
              )}
            </div>

            <div className="form-group">
              <label>3D Inspect URL</label>
              <input
                ref={objectUrlInputRef}
                type="text"
                className="assign-select-full"
                value={threeDInspectUrl}
                onChange={(e) => {
                  if (objectUrlHint) setObjectUrlHint('');
                  setThreeDInspectUrl(e.target.value);
                }}
                onBlur={() => {
                  const r = validateObjectUrl(threeDInspectUrl);
                  if (!r.ok) {
                    setObjectUrlHint(r.hint);
                    setTimeout(() => objectUrlInputRef.current?.focus(), 0);
                    return;
                  }
                  setObjectUrlHint('');
                  if (r.cleaned !== threeDInspectUrl) setThreeDInspectUrl(r.cleaned);
                }}
                placeholder="https://www.terra-3dinspect.com/objects/<object-uuid>"
              />
              {objectUrlHint && (
                <div className="form-field-hint-error">{objectUrlHint}</div>
              )}
            </div>

            {/* Danger zone — hidden in read-only ("Show more") */}
            {!readOnly && (
            <div className="eom-danger-zone">
              <p className="eom-danger-desc">
                Permanently delete this object.
                {inspectionCount > 0
                  ? (isAdmin
                      ? ` Deleting also removes all ${inspectionCount} inspection${inspectionCount !== 1 ? 's' : ''} inside.`
                      : ` Remove all ${inspectionCount} inspection${inspectionCount !== 1 ? 's' : ''} before deleting.`)
                  : ' This cannot be undone.'}
              </p>
              <button
                className="btn btn-outline-danger btn-sm"
                onClick={handleDeleteObject}
                disabled={inspectionCount === null || (inspectionCount > 0 && !isAdmin) || deleting || saving}
                title={
                  inspectionCount > 0 && !isAdmin
                    ? `Cannot delete: object has ${inspectionCount} inspection${inspectionCount !== 1 ? 's' : ''}`
                    : 'Delete this object permanently'
                }
              >
                {deleting ? 'Deleting…' : 'Delete Object'}
              </button>
            </div>
            )}
          </div>
        )}

        {/* ── Page 1: Design ── */}
        {page === 1 && (
          <div className="eom-page">
            <h3 className="eom-page-title">Design</h3>
            <div className="form-group">
              <label>Object Specification</label>
              <select
                className="assign-select-full"
                value={objectSpec}
                onChange={(e) => handleDropdownChange('design_objectSpec', e.target.value, setObjectSpec)}
              >
                <option value="">— Select —</option>
                {getComputedOptions('design_objectSpec').map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Attribute A</label>
                <select
                  className="assign-select-full"
                  value={attributeA}
                  onChange={(e) => handleDropdownChange('design_attributeA', e.target.value, setAttributeA)}
                >
                  <option value="">— Select —</option>
                  {getComputedOptions('design_attributeA').map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Attribute A Type</label>
                <select
                  className="assign-select-full"
                  value={attributeAType}
                  onChange={(e) => handleDropdownChange('design_attributeAType', e.target.value, setAttributeAType)}
                >
                  <option value="">— Select —</option>
                  {getComputedOptions('design_attributeAType').map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Attribute B</label>
                <select
                  className="assign-select-full"
                  value={attributeB}
                  onChange={(e) => handleDropdownChange('design_attributeB', e.target.value, setAttributeB)}
                >
                  <option value="">— Select —</option>
                  {getComputedOptions('design_attributeB').map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Attribute B Type</label>
                <select
                  className="assign-select-full"
                  value={attributeBType}
                  onChange={(e) => handleDropdownChange('design_attributeBType', e.target.value, setAttributeBType)}
                >
                  <option value="">— Select —</option>
                  {getComputedOptions('design_attributeBType').map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Product Stored */}
            <div className="form-group">
              <label>Product Stored</label>
              <span className="eom-product-hint">
                Select a radio option or double-click the field to type.
              </span>
              <input
                ref={productInputRef}
                type="text"
                className={`eom-product-input${!textEditable ? ' eom-product-locked' : ''}`}
                value={productText}
                onChange={handleProductTextChange}
                onDoubleClick={handleProductDoubleClick}
                placeholder={textEditable ? 'Type product name…' : 'Double-click to enter custom value'}
                readOnly={!textEditable}
                title={!textEditable ? 'Double-click to clear and type a custom value' : ''}
              />
              <div className="eom-product-row">
                <label className="eom-radio-label">
                  <input
                    type="radio"
                    name="productStored"
                    checked={radioVal === 'Not available'}
                    onChange={() => handleRadioChange('Not available')}
                  />
                  Not available
                </label>
                <label className="eom-radio-label">
                  <input
                    type="radio"
                    name="productStored"
                    checked={radioVal === 'Not applicable'}
                    onChange={() => handleRadioChange('Not applicable')}
                  />
                  Not applicable
                </label>
              </div>
            </div>
          </div>
        )}

        {/* ── Page 2: Sizing ── */}
        {page === 2 && (
          <div className="eom-page">
            <h3 className="eom-page-title">Sizing</h3>

            <div className="form-group">
              <label>Dimension A</label>
              <select
                className="assign-select-full"
                value={dimensionA}
                onChange={(e) => handleDropdownChange('sizing_dimensionA', e.target.value, setDimensionA)}
              >
                <option value="">— Select —</option>
                {getComputedOptions('sizing_dimensionA').map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Dimension A Size (mm)</label>
              <div className="eom-na-row">
                <label className="eom-checkbox-label">
                  <input
                    type="checkbox"
                    checked={dimASizeNA}
                    onChange={(e) => handleDimASizeNA(e.target.checked)}
                  />
                  Not available
                </label>
              </div>
              <input
                type="text"
                className={`eom-product-input${dimASizeNA ? ' eom-product-locked' : ''}`}
                value={dimASizeNA ? 'Not available' : dimASize}
                readOnly={dimASizeNA}
                onChange={(e) => { if (!dimASizeNA) setDimASize(e.target.value); }}
                onKeyDown={(e) => {
                  if (dimASizeNA) return;
                  if (e.ctrlKey || e.metaKey || e.key.length > 1) return;
                  if (!/[0-9]/.test(e.key)) e.preventDefault();
                }}
                placeholder={dimASizeNA ? '' : 'Enter size in mm…'}
              />
            </div>

            <div className="form-group">
              <label>Dimension B</label>
              <select
                className="assign-select-full"
                value={dimensionB}
                onChange={(e) => handleDropdownChange('sizing_dimensionB', e.target.value, setDimensionB)}
              >
                <option value="">— Select —</option>
                {getComputedOptions('sizing_dimensionB').map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Dimension B Size (mm)</label>
              <div className="eom-na-row">
                <label className="eom-checkbox-label">
                  <input
                    type="checkbox"
                    checked={dimBSizeNA}
                    onChange={(e) => handleDimBSizeNA(e.target.checked)}
                  />
                  Not available
                </label>
              </div>
              <input
                type="text"
                className={`eom-product-input${dimBSizeNA ? ' eom-product-locked' : ''}`}
                value={dimBSizeNA ? 'Not available' : dimBSize}
                readOnly={dimBSizeNA}
                onChange={(e) => { if (!dimBSizeNA) setDimBSize(e.target.value); }}
                onKeyDown={(e) => {
                  if (dimBSizeNA) return;
                  if (e.ctrlKey || e.metaKey || e.key.length > 1) return;
                  if (!/[0-9]/.test(e.key)) e.preventDefault();
                }}
                placeholder={dimBSizeNA ? '' : 'Enter size in mm…'}
              />
            </div>

            <div className="form-group">
              <label>Object Closely Resembles</label>
              <select
                className="assign-select-full"
                value={objectCloselyResembles}
                onChange={(e) => handleDropdownChange('sizing_objectCloselyResembles', e.target.value, setObjectCloselyResembles)}
              >
                <option value="">— Select —</option>
                {getComputedOptions('sizing_objectCloselyResembles').map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>

            {/* A / B / C inputs (left) + image placeholder (right) */}
            <div className="eom-abc-row">
              <div className="eom-abc-left">
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>A (mm)</label>
                  <input
                    type="text"
                    className={`eom-product-input${!aEnabled ? ' eom-product-locked' : ''}`}
                    value={aEnabled ? sizingA : '-'}
                    disabled={!aEnabled}
                    onChange={(e) => { if (aEnabled) setSizingA(e.target.value); }}
                    onKeyDown={(e) => {
                      if (!aEnabled) return;
                      if (e.ctrlKey || e.metaKey || e.key.length > 1) return;
                      if (!/[0-9]/.test(e.key)) e.preventDefault();
                    }}
                    placeholder={aEnabled ? '0' : ''}
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>B (mm)</label>
                  <input
                    type="text"
                    className={`eom-product-input${!bEnabled ? ' eom-product-locked' : ''}`}
                    value={bEnabled ? sizingB : '-'}
                    disabled={!bEnabled}
                    onChange={(e) => { if (bEnabled) setSizingB(e.target.value); }}
                    onKeyDown={(e) => {
                      if (!bEnabled) return;
                      if (e.ctrlKey || e.metaKey || e.key.length > 1) return;
                      if (!/[0-9]/.test(e.key)) e.preventDefault();
                    }}
                    placeholder={bEnabled ? '0' : ''}
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>C (mm)</label>
                  <input
                    type="text"
                    className={`eom-product-input${!cEnabled ? ' eom-product-locked' : ''}`}
                    value={cEnabled ? sizingC : '-'}
                    disabled={!cEnabled}
                    onChange={(e) => { if (cEnabled) setSizingC(e.target.value); }}
                    onKeyDown={(e) => {
                      if (!cEnabled) return;
                      if (e.ctrlKey || e.metaKey || e.key.length > 1) return;
                      if (!/[0-9]/.test(e.key)) e.preventDefault();
                    }}
                    placeholder={cEnabled ? '0' : ''}
                  />
                </div>
              </div>
              <div className="eom-sizing-image-ph">
                <img
                  src={shapeImageSrc}
                  alt={objectCloselyResembles || 'No shape selected'}
                  className="eom-sizing-image"
                />
              </div>
            </div>

            {/* Estimated surface area and volume — display only, full width */}
            <div className="form-group">
              <label>Estimated Surface Area (m²)</label>
              <input
                type="text"
                className="eom-product-input eom-product-locked"
                value={estSA === null || estSA === undefined ? '-' : String(Math.round(estSA))}
                readOnly
                disabled
              />
            </div>
            <div className="form-group">
              <label>Estimated Volume (m³)</label>
              <input
                type="text"
                className="eom-product-input eom-product-locked"
                value={estVol === null || estVol === undefined ? '-' : String(Math.round(estVol))}
                readOnly
                disabled
              />
            </div>
          </div>
        )}

        {/* ── Page 3: Construction ── */}
        {page === 3 && (
          <div className="eom-page">
            <h3 className="eom-page-title">Construction</h3>
            <div className="form-group">
              <label>Building Standard</label>
              <select
                className="assign-select-full"
                value={buildingStandard}
                onChange={(e) => handleDropdownChange('construction_buildingStandard', e.target.value, setBuildingStandard)}
              >
                <option value="">— Select —</option>
                {getComputedOptions('construction_buildingStandard').map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Material 1</label>
                <select
                  className="assign-select-full"
                  value={material1}
                  onChange={(e) => handleDropdownChange('construction_material1', e.target.value, setMaterial1)}
                >
                  <option value="">— Select —</option>
                  {getComputedOptions('construction_material1').map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Material 1 Type</label>
                <select
                  className="assign-select-full"
                  value={material1Type}
                  onChange={(e) => handleDropdownChange('construction_material1Type', e.target.value, setMaterial1Type)}
                >
                  <option value="">— Select —</option>
                  {getComputedOptions('construction_material1Type').map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="eom-mat2-header">
              <label className="eom-checkbox-label">
                <input
                  type="checkbox"
                  checked={mat2NotApplicable}
                  onChange={(e) => handleMat2NotApplicable(e.target.checked)}
                />
                Second material not applicable
              </label>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Material 2</label>
                <select
                  className="assign-select-full"
                  value={material2}
                  disabled={mat2NotApplicable}
                  onChange={(e) => handleDropdownChange('construction_material2', e.target.value, setMaterial2)}
                >
                  <option value="">— Select —</option>
                  {getComputedOptions('construction_material2').map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Material 2 Type</label>
                <select
                  className="assign-select-full"
                  value={material2Type}
                  disabled={mat2NotApplicable}
                  onChange={(e) => handleDropdownChange('construction_material2Type', e.target.value, setMaterial2Type)}
                >
                  <option value="">— Select —</option>
                  {getComputedOptions('construction_material2Type').map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Insulation</label>
              <select
                className="assign-select-full"
                value={insulation}
                onChange={(e) => handleDropdownChange('construction_insulation', e.target.value, setInsulation)}
              >
                <option value="">— Select —</option>
                {getComputedOptions('construction_insulation').map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Manhole Angle from True North (°)</label>
              <span className="eom-product-hint">
                Clockwise angle (0–359°) from True North to the manhole, viewed from the top.
              </span>
              <input
                type="text"
                inputMode="numeric"
                className="eom-product-input"
                value={manholeNorthAngle}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === '') { setManholeNorthAngle(''); return; }
                  if (!/^\d+$/.test(raw)) return;
                  const n = parseInt(raw, 10);
                  if (n < 0 || n > 359) return;
                  setManholeNorthAngle(String(n));
                }}
                onKeyDown={(e) => {
                  if (e.ctrlKey || e.metaKey || e.key.length > 1) return;
                  if (!/[0-9]/.test(e.key)) e.preventDefault();
                }}
                placeholder="0–359"
              />
            </div>
          </div>
        )}

        {/* ── Page 4: Miscellaneous ── */}
        {page === 4 && (
          <div className="eom-page">
            <h3 className="eom-page-title">Miscellaneous</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Heating Element</label>
                <select
                  className="assign-select-full"
                  value={heatingElement}
                  onChange={(e) => handleDropdownChange('misc_heatingElement', e.target.value, setHeatingElement)}
                >
                  <option value="">— Select —</option>
                  {getComputedOptions('misc_heatingElement').map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Structural Support</label>
                <select
                  className="assign-select-full"
                  value={structuralSupport}
                  onChange={(e) => handleDropdownChange('misc_structuralSupport', e.target.value, setStructuralSupport)}
                >
                  <option value="">— Select —</option>
                  {getComputedOptions('misc_structuralSupport').map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Design Pressure */}
            <div className="form-group">
              <label>Design Pressure (mbar)</label>
              <span className="eom-product-hint">
                Select a radio option or double-click the field to type.
              </span>
              <input
                ref={designPressureRef}
                type="text"
                className={`eom-product-input${!designPressureEditable ? ' eom-product-locked' : ''}`}
                value={designPressureText}
                onChange={handleDesignPressureTextChange}
                onDoubleClick={handleDesignPressureDoubleClick}
                onKeyDown={(e) => {
                  if (!designPressureEditable) return;
                  if (e.ctrlKey || e.metaKey || e.key.length > 1) return;
                  if (!/[0-9.+\-/]/.test(e.key)) e.preventDefault();
                }}
                placeholder={designPressureEditable ? 'Type pressure value…' : 'Double-click to enter custom value'}
                readOnly={!designPressureEditable}
                title={!designPressureEditable ? 'Double-click to clear and type a custom value' : ''}
              />
              <div className="eom-product-row">
                <label className="eom-radio-label">
                  <input
                    type="radio"
                    name="designPressure"
                    checked={designPressureRadio === 'Not available'}
                    onChange={() => handleDesignPressureRadio('Not available')}
                  />
                  Not available
                </label>
                <label className="eom-radio-label">
                  <input
                    type="radio"
                    name="designPressure"
                    checked={designPressureRadio === 'Not applicable'}
                    onChange={() => handleDesignPressureRadio('Not applicable')}
                  />
                  Not applicable
                </label>
              </div>
            </div>

            {/* Design Temperature */}
            <div className="form-group">
              <label>Design Temperature (°C)</label>
              <span className="eom-product-hint">
                Select a radio option or double-click the field to type.
              </span>
              <input
                ref={designTempRef}
                type="text"
                className={`eom-product-input${!designTempEditable ? ' eom-product-locked' : ''}`}
                value={designTempText}
                onChange={handleDesignTempTextChange}
                onDoubleClick={handleDesignTempDoubleClick}
                onKeyDown={(e) => {
                  if (!designTempEditable) return;
                  if (e.ctrlKey || e.metaKey || e.key.length > 1) return;
                  if (!/[0-9.+\-/]/.test(e.key)) e.preventDefault();
                }}
                placeholder={designTempEditable ? 'Type temperature value…' : 'Double-click to enter custom value'}
                readOnly={!designTempEditable}
                title={!designTempEditable ? 'Double-click to clear and type a custom value' : ''}
              />
              <div className="eom-product-row">
                <label className="eom-radio-label">
                  <input
                    type="radio"
                    name="designTemp"
                    checked={designTempRadio === 'Not available'}
                    onChange={() => handleDesignTempRadio('Not available')}
                  />
                  Not available
                </label>
                <label className="eom-radio-label">
                  <input
                    type="radio"
                    name="designTemp"
                    checked={designTempRadio === 'Not applicable'}
                    onChange={() => handleDesignTempRadio('Not applicable')}
                  />
                  Not applicable
                </label>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Year of Construction</label>
                <select
                  className="assign-select-full"
                  value={yearOfConstruction}
                  onChange={(e) => handleDropdownChange('misc_yearOfConstruction', e.target.value, setYearOfConstruction)}
                >
                  <option value="">— Select —</option>
                  {[...getComputedOptions('misc_yearOfConstruction')].reverse().map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Previous TI Inspection Year</label>
                <select
                  className="assign-select-full"
                  value={previousTIYear}
                  onChange={(e) => handleDropdownChange('misc_previousTIYear', e.target.value, setPreviousTIYear)}
                >
                  <option value="">— Select —</option>
                  {[...getComputedOptions('misc_previousTIYear')].reverse().map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        </fieldset>

        {/* Footer */}
        {readOnly ? (
          <div className="eom-footer">
            <div className="eom-footer-left" />
            <div className="eom-footer-right">
              {page > 0 && (
                <button className="btn btn-secondary" onClick={() => setPage((p) => p - 1)}>← Back</button>
              )}
              {page < PAGES.length - 1 ? (
                <button className="btn btn-primary" onClick={() => setPage((p) => p + 1)}>Next →</button>
              ) : (
                <button className="btn btn-primary" onClick={onClose}>Close</button>
              )}
            </div>
          </div>
        ) : (
          <div className="eom-footer">
            <div className="eom-footer-left">
              <button className="btn btn-secondary" onClick={onClose} disabled={saving || deleting}>
                Cancel
              </button>
            </div>
            <div className="eom-footer-right">
              {page > 0 && (
                <button className="btn btn-secondary" onClick={() => setPage((p) => p - 1)} disabled={saving || deleting}>
                  ← Back
                </button>
              )}
              {page < PAGES.length - 1 ? (
                <button className="btn btn-primary" onClick={() => setPage((p) => p + 1)}>
                  Next →
                </button>
              ) : (
                <button className="btn btn-primary eom-save-btn" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
              )}
            </div>
          </div>
        )}

        {saveError && <div className="alert alert-danger" style={{ margin: '0 24px 16px' }}>{saveError}</div>}

      </div>

      {showDeleteDenied && (
        <AccessDeniedModal
          roles={userProfile?.roles || []}
          onClose={() => setShowDeleteDenied(false)}
        />
      )}

      {showDeleteWithInspections && (
        <div className="modal-overlay" onClick={() => !deleting && setShowDeleteWithInspections(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">Delete object with inspections?</h2>
            <p className="modal-body-text">
              <strong>{infoObjectTag}</strong> still has {inspectionCount} inspection{inspectionCount !== 1 ? 's' : ''}.
              Deleting the object will <strong>permanently delete {inspectionCount !== 1 ? 'them' : 'it'} too</strong>. This cannot be undone.
            </p>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowDeleteWithInspections(false)} disabled={deleting}>Cancel</button>
              <button className="btn btn-danger" onClick={performDelete} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete anyway'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    {showDiscard && (
      <DiscardConfirmModal
        onDiscard={() => { setShowDiscard(false); onClose(); }}
        onKeepEditing={() => setShowDiscard(false)}
      />
    )}
    </>
  );
}
